import React, {useState, useEffect} from 'react';
import {
    Button,
    Card,
    Checkbox,
    Col,
    Divider,
    Form,
    Input,
    InputNumber,
    List,
    message,
    Radio,
    Row,
    Space,
    Spin,
    Steps,
    Typography
} from 'antd';
import {
    CalculatorOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    SaveOutlined,
    SendOutlined
} from '@ant-design/icons';
import AdminLayout from "@/components/layout/admin/adminLayout";
import {URLS} from "@/lib/urls";
import {useApiCalculateAdminProductDeliveryStep, useApiCreateAdminProductDeliveryStep} from "@/api/product";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import TaggableSelect from "@/components/select/apiSelect";
import {useApiSearchAdminTopics} from "@/api/topic";
import toFaNumber from "@/lib/utils/number";
import {useNavigate, useParams} from "react-router-dom";
import {ListStartIcon} from "lucide-react";
import RenderIf from "@/components/render-if";
import ProductDeliverStepCreateCalculateResult
    from "@/pages/admin/products/delivery/create-step/_section/ProductDeliverStepCreateCalculateResult";

const {TextArea} = Input;
const {Title, Text} = Typography;

function AdminProductDeliveryStepCreate(props) {
    const [form] = Form.useForm();
    const {id} = useParams();
    const navigate = useNavigate();

    const [topics, setTopics] = useState([]);
    const [step, setStep] = useState('calculate');
    const [calculationResult, setCalculationResult] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [deliverType, setDeliverType] = useState('user');

    // ✅ state برای محصولات (سرفصل‌های) انتخاب شده برای تحویل
    const [selectedProducts, setSelectedProducts] = useState([]);

    const {mutate: calculateDelivery, isPending: isPendingCalculate} = useApiCalculateAdminProductDeliveryStep();
    const {mutate: createDelivery, isPending: isPendingCreate} = useApiCreateAdminProductDeliveryStep();

    const handleCalculate = async (values) => {
        if (!values.topics || values.topics.length === 0) {
            message.warning('لطفاً حداقل یک سرفصل را انتخاب کنید');
            return;
        }

        setIsCalculating(true);

        calculateDelivery({
            product_id: id,
            topics: values.topics,
            count: values.count,
        }, {
            onSuccess: (response) => {
                setIsCalculating(false);
                if (response?.data?.data) {
                    const data = response.data.data;
                    setCalculationResult(data);
                    setStep('submit');
                    message.success('محاسبه با موفقیت انجام شد');

                    // ✅ مقداردهی اولیه به محصولات انتخاب شده (پیش‌فرض همه)
                    const items = data?.items || data?.topics || data?.products || [];
                    if (items.length > 0) {
                        setSelectedProducts(items.map(item => item.id));
                    } else {
                        // اگه لیست آیتم‌ها نبود، از همون topics انتخاب شده استفاده کن
                        setSelectedProducts(values.topics || []);
                    }

                    form.setFieldsValue({
                        topics: values.topics,
                        count: data.current_count || 0,
                    });
                }
            },
            onError: (error) => {
                setIsCalculating(false);
                ApiHandleErrorMessage(error);
            }
        });
    };


    const handleSubmit = async (values) => {
        // ✅ اعتبارسنجی: حداقل یک محصول باید انتخاب شده باشد
        if (selectedProducts.length === 0) {
            message.warning('لطفاً حداقل یک محصول را برای تحویل انتخاب کنید');
            return;
        }

        const submitData = {
            product_id: id,
            topics: topics,
            count_male: values.count_male,
            count_female: values.count_female,
            description: values.description || null,
            deliver_to_lesson: deliverType === 'lesson',
            lesson_count: values.lesson_count,
            products_ids: selectedProducts,
        };

        createDelivery(submitData, {
            onSuccess: (response) => {
                ApiHandleSuccessMessage(response);
                navigate(URLS.admin.products.delivery.list);
            },
            onError: (error) => {
                ApiHandleErrorMessage(error);
            }
        });
    };


    const handleBackToCalculate = () => {
        setStep('calculate');
        setCalculationResult(null);
        setSelectedProducts([]);
        form.resetFields(['count', 'description', 'notif_text', 'send_notif']);
    };


    const handleTopicsChange = (newTopics) => {
        setTopics(newTopics);

        if (step === 'submit') {
            setStep('calculate');
            setCalculationResult(null);
            setSelectedProducts([]);
            form.resetFields(['count', 'description', 'notif_text', 'send_notif']);
        }
    };


    // ============================================================
    // ✅ بخش نمایش لیست محصولات با قابلیت تیک زدن
    // ============================================================
    const renderProductsList = () => {
        // استخراج لیست آیتم‌ها از calculationResult
        const items = calculationResult?.items
            || calculationResult?.topics
            || calculationResult?.products
            || [];

        if (items.length === 0) {
            return null;
        }

        const allSelected = selectedProducts.length === items.length;
        const isIndeterminate = selectedProducts.length > 0 && selectedProducts.length < items.length;

        const handleToggle = (itemId, checked) => {
            if (checked) {
                setSelectedProducts(prev => [...prev, itemId]);
            } else {
                setSelectedProducts(prev => prev.filter(x => x !== itemId));
            }
        };

        const handleToggleAll = (checked) => {
            if (checked) {
                setSelectedProducts(items.map(item => item.id));
            } else {
                setSelectedProducts([]);
            }
        };

        return (
            <Card className="mb-4 shadow-sm rounded-lg" size="small">
                <div className="flex justify-between items-center mb-3">
                    <Text strong>لیست محصولات قابل تحویل</Text>
                    <Checkbox
                        indeterminate={isIndeterminate}
                        checked={allSelected}
                        onChange={(e) => handleToggleAll(e.target.checked)}
                    >
                        انتخاب همه
                    </Checkbox>
                </div>

                <List
                    dataSource={items}
                    renderItem={(item) => {
                        const isSelected = selectedProducts.includes(item.id);
                        return (
                            <List.Item
                                className={`
                                    transition-all rounded-lg mb-2 px-3
                                    ${!isSelected
                                    ? ' opacity-60 border dark:border-gray-600 border-dashed border-gray-300'
                                    : ' border dark:border-gray-600 border-gray-200'
                                }
                                `}
                                style={{cursor: 'pointer'}}
                                onClick={() => handleToggle(item.id, !isSelected)}
                            >
                                <div className="flex items-center gap-3 w-full px-2">
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleToggle(item.id, e.target.checked);
                                        }}
                                    />
                                    <div className="flex-1 flex items-center justify-between">
                                        <Text
                                            delete={!isSelected}
                                            className={!isSelected ? ' ' : ''}
                                        >
                                            {item.title || `محصول ${item.id}`}
                                        </Text>
                                        {!isSelected && (
                                            <Text className="text-xs text-red-500 mr-2">
                                                (تحویل داده نمی‌شود)
                                            </Text>
                                        )}
                                    </div>
                                </div>
                            </List.Item>
                        );
                    }}
                />
            </Card>
        );
    };


    if (initialLoading) {
        return (
            <AdminLayout pageTitle={'مرحله ارسال محصول'} backTo={URLS.admin.products.list}>
                <Card className="shadow-lg rounded-xl">
                    <div className="flex justify-center items-center h-64">
                        <Spin
                            indicator={<LoadingOutlined style={{fontSize: 48}} spin/>}
                            tip="در حال بارگذاری..."
                        />
                    </div>
                </Card>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout
            pageTitle={`ایجاد گروه تحویل`}
            backTo={URLS.admin.products.delivery.list}
        >
            <Card className="shadow-lg rounded-xl">

                <div className="mb-6">
                    <Steps
                        current={step === 'calculate' ? 1 : 2}
                        items={[
                            {
                                key: 'start',
                                title: 'شروع',
                                icon: <ListStartIcon/>,
                            },
                            {
                                key: 'calculate',
                                title: 'محاسبه',
                                icon: <CalculatorOutlined/>,
                            },
                            {
                                key: 'submit',
                                title: 'ثبت ارسال',
                                icon: <SendOutlined/>,
                            },
                        ]}
                    />
                </div>


                {step === 'calculate' && (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleCalculate}
                        className="space-y-4"
                        initialValues={{
                            topics: [],
                        }}
                    >
                        <div className="mb-4 p-4 bg-blue-50 rounded-lg my-2 border dark:border-gray-600 border-blue-200">
                            <Space>
                                <InfoCircleOutlined className="text-blue-500"/>
                                <Text className="text-blue-700">
                                    ابتدا تعداد افراد و سرفصل را جهت محاسبه وارد کنید
                                </Text>
                            </Space>
                        </div>


                        <Form.Item
                            name="topics"
                            rules={[
                                {required: true, message: 'انتخاب حداقل یک سرفصل الزامی است'},
                                {type: 'array', message: 'سرفصل‌ها باید آرایه باشند'},
                            ]}
                            className="mb-4"
                        >
                            <TaggableSelect
                                name="topic-search"
                                className={'mt-2'}
                                label={'انتخاب سرفصل'}
                                searchFn={useApiSearchAdminTopics}
                                onChange={handleTopicsChange}
                                values={topics}
                                render={(item) => {
                                    return toFaNumber(item.id) + ' - ' + item.title
                                }}
                                initialValues={topics}
                                placeholder="سرفصل‌های مورد نظر را انتخاب کنید"
                            />
                        </Form.Item>
                        <Form.Item
                            name="count"
                            label="تعداد افراد "
                            rules={[
                                {required: true, message: 'تعداد ارسال الزامی است'},
                            ]}
                            className="mb-4"
                        >
                            <InputNumber
                                className="w-full rounded-lg"
                                placeholder="تعداد افراد"
                                min={1}
                                suffix={'نفر'}
                                step={1}
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                            />
                        </Form.Item>

                        <Form.Item className="mb-0">
                            <Space className="w-full justify-between" size="middle">
                                <Button
                                    onClick={() => navigate(URLS.admin.products.list)}
                                >
                                    انصراف
                                </Button>

                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={isPendingCalculate || isCalculating}
                                    icon={<CalculatorOutlined/>}
                                >
                                    {isPendingCalculate || isCalculating ? 'در حال محاسبه...' : 'محاسبه تعداد قابل ارسال'}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                )}


                {step === 'submit' && calculationResult && (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        className="space-y-4"
                    >

                        <ProductDeliverStepCreateCalculateResult calculationResult={calculationResult}
                                                                 handleBackToCalculate={handleBackToCalculate} />

                        {/* ✅ نمایش لیست محصولات با قابلیت تیک زدن */}
                        {renderProductsList()}


                        <Radio.Group
                            block
                            options={[
                                {label: 'تحویل به اعضا', value: 'user'},
                                {label: 'تحویل به مربی کلاس', value: 'lesson'},
                            ]}
                            onChange={(e) => setDeliverType(e.target.value)}
                            defaultValue={deliverType}
                            optionType="button"
                            buttonStyle="solid"
                        />

                        <RenderIf condition={deliverType === 'user'}>
                            <Row gutter={24}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="count_male"
                                        label="تعداد پسر "
                                        rules={[
                                            {required: true, message: 'تعداد افراد الزامی است'},
                                        ]}
                                        className="mb-4"
                                    >
                                        <InputNumber
                                            className="w-full rounded-lg"
                                            placeholder="تعداد پسر"
                                            min={0}

                                            step={1}
                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="count_female"
                                        label="تعداد دختر "
                                        rules={[
                                            {required: true, message: 'تعداد افراد الزامی است'},
                                        ]}
                                        className="mb-4"
                                    >
                                        <InputNumber
                                            className="w-full rounded-lg"
                                            placeholder="تعداد دختر"
                                            min={0}

                                            step={1}
                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </RenderIf>

                        <RenderIf condition={deliverType === 'lesson'}>
                            <Form.Item
                                name="lesson_count"
                                label="تعداد کلاس "
                                rules={[
                                    {required: true, message: 'تعداد کلاس الزامی است'},
                                ]}
                                className="mb-4"
                            >
                                <InputNumber
                                    className="w-full rounded-lg"
                                    placeholder="تعداد کلاس"
                                    min={0}

                                    step={1}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </RenderIf>


                        <Form.Item
                            name="description"
                            label="توضیحات"
                            rules={[
                                {required: false},
                                {type: 'string', message: 'توضیحات باید متن باشد'},
                            ]}
                            className="mb-4"
                        >
                            <Input.TextArea
                                rows={3}
                                placeholder="توضیحات اضافی (اختیاری) مانند: سری اول توزیع اقلام در تاریخ ... "
                                className="rounded-lg"
                            />
                        </Form.Item>

                        <Divider/>

                        <Form.Item className="mb-0">
                            <Space className="w-full justify-between" size="middle">
                                <Space>
                                    <Button
                                        onClick={handleBackToCalculate}
                                    >
                                        بازگشت به محاسبه
                                    </Button>
                                    <Button
                                        onClick={() => navigate(URLS.admin.products.list)}
                                    >
                                        انصراف
                                    </Button>
                                </Space>

                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={isPendingCreate}
                                    icon={<SaveOutlined/>}
                                    disabled={selectedProducts.length === 0}
                                >
                                    {isPendingCreate ? 'در حال ثبت...' : 'ثبت گروه تحویل'}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                )}
            </Card>
        </AdminLayout>
    );
}

export default AdminProductDeliveryStepCreate;